import { MediaPicker } from "../../components/MediaPicker";
import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { WorkflowPanel } from "./AdminReview";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

const EMPTY = { quote: "", author_name: "", author_role: "", image_url: "", image_is_real: false, is_active: true, order: 0 };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => { const r = await api.get("/testimonials"); setItems(r.data); };
  useEffect(() => { document.title = "Admin · Testimonials"; load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) { await api.put(`/admin/testimonials/${editing.id}`, editing); toast.success("Updated"); }
      else { await api.post("/admin/testimonials", editing); toast.success("Created"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl font-bold">Testimonials</h1>
          <p className="text-[#0a0d12]/60 text-sm">Stories from beneficiaries shown on home and impact pages.</p>
        </div>
        <button onClick={() => { setEditing({ ...EMPTY }); setOpen(true); }} className="dk-btn-primary !py-2.5 !px-4 text-sm" data-testid="add-testimonial-btn">
          <Plus size={16} /> Add testimonial
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} className="bg-white rounded-xl border border-black/5 p-5">
            <p className="font-serif text-[#0a0d12] mb-3">"{t.quote}"</p>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-sm">{t.author_name}</div>
                <div className="text-xs text-[#0a0d12]/60">{t.author_role}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditing(t); setOpen(true); }} className="p-1.5 hover:bg-[#f8f4e8] rounded"><Edit2 size={14} /></button>
                <button onClick={() => remove(t.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-[#0a0d12]/50">No testimonials yet.</p>}
      </div>

      {open && editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-7 max-w-xl w-full" data-testid="testimonial-form">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl font-bold">{editing.id ? "Edit testimonial" : "Add testimonial"}</h2>
              <button type="button" onClick={() => setOpen(false)}><X size={20} /></button>
            </div>
            {editing.id && <div className="mb-4"><WorkflowPanel collection="testimonials" recordId={editing.id} onChanged={load} /></div>}
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold mb-1 block">Quote *</label>
                <textarea required rows={3} value={editing.quote} onChange={(e) => setEditing({ ...editing, quote: e.target.value })} className="w-full px-4 py-2.5 border border-black/15 rounded-md" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold mb-1 block">Author *</label>
                  <input required value={editing.author_name} onChange={(e) => setEditing({ ...editing, author_name: e.target.value })} className="w-full px-4 py-2.5 border border-black/15 rounded-md" /></div>
                <div><label className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold mb-1 block">Role</label>
                  <input value={editing.author_role} onChange={(e) => setEditing({ ...editing, author_role: e.target.value })} className="w-full px-4 py-2.5 border border-black/15 rounded-md" /></div>
              </div>
              <div><label className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold mb-1 block">Image URL</label>
                <div className="flex items-center gap-2"><input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} className="w-full px-4 py-2.5 border border-black/15 rounded-md" /><MediaPicker value={editing.image_url} onChange={(u) => setEditing({ ...editing, image_url: u })} testid="testimonial-image-upload" /></div></div>
              <div className="flex items-center gap-4">
                <label className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold">Order
                  <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} className="ml-2 w-20 px-3 py-1 border border-black/15 rounded-md" /></label>
                <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active</label>
                <label className="text-sm flex items-center gap-2" title="Tick only if this is a real photograph of the person quoted and written consent is on file"><input type="checkbox" checked={!!editing.image_is_real} onChange={(e) => setEditing({ ...editing, image_is_real: e.target.checked })} data-testid="testimonial-image-real" /> Real photo with consent (otherwise labelled illustrative)</label>
              </div>
            </div>
            <button type="submit" className="dk-btn-primary mt-6">Save</button>
          </form>
        </div>
      )}
    </div>
  );
}
